import { useEffect } from 'react';
import type { MeResponse } from '@/lib/api';
import { Icon } from '@/components/Icon';
import { ShushMark } from '@/components/ShushMark';
import { formatExpiresShort } from '@/lib/format';
import { haptic } from '@/lib/telegram';

interface SuccessScreenProps {
  me: MeResponse;
  method: 'stars' | 'ton';
  onInstall: () => void;
  onHome: () => void;
}

// Russian price formatter — keeps the receipt copy in sync with paywall.
function methodLabel(method: 'stars' | 'ton'): string {
  return method === 'stars' ? 'telegram stars' : 'ton';
}

function paidAmount(me: MeResponse, method: 'stars' | 'ton'): string {
  const { pricing } = me;
  if (method === 'stars') {
    const final = pricing.stars.final_amount;
    return pricing.stars.discount_pct > 0
      ? `⭐ ${final} (−${pricing.stars.discount_pct}%)`
      : `⭐ ${final}`;
  }
  const cents = pricing.ton.final_usd_cents;
  const whole = Math.floor(cents / 100);
  const fraction = cents % 100;
  const amount = `$${whole}.${String(fraction).padStart(2, '0')}`;
  return pricing.ton.discount_pct > 0
    ? `${amount} (−${pricing.ton.discount_pct}%)`
    : amount;
}

export function SuccessScreen({ me, method, onInstall, onHome }: SuccessScreenProps) {
  useEffect(() => {
    haptic('medium');
  }, []);

  const expires = formatExpiresShort(me.subscription.expires_at);
  const daysLabel = `${me.pricing.duration_days} дней`;

  return (
    <div
      className="no-scrollbar"
      style={{ height: '100%', overflowY: 'auto', position: 'relative' }}
    >
      {/* paper texture top — soft moss wash, matches design handoff */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 320,
          background: 'linear-gradient(180deg, var(--moss-soft) 0%, var(--paper) 100%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1, padding: '38px 22px 100px' }}>
        {/* mark with halo + small check badge */}
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 999,
            background: '#fff',
            border: '1px solid var(--paper-3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 28px',
            boxShadow: '0 4px 20px color-mix(in oklab, var(--moss) 18%, transparent)',
            position: 'relative',
          }}
        >
          <ShushMark size={48} color="var(--moss-deep)" accent="var(--moss)" />
          <span
            style={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: 28,
              height: 28,
              borderRadius: 999,
              background: 'var(--moss)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid var(--paper)',
            }}
          >
            <Icon name="check" size={14} stroke="#fff" sw={2.5} />
          </span>
        </div>

        <h1
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: 40,
            lineHeight: 1.0,
            textAlign: 'center',
            margin: '0 0 12px',
            letterSpacing: '-0.02em',
            color: 'var(--ink)',
          }}
        >
          интернет
          <br />
          <span style={{ fontStyle: 'italic', color: 'var(--moss-deep)' }}>вернулся</span>
        </h1>
        <p
          style={{
            textAlign: 'center',
            fontSize: 13.5,
            color: 'var(--ink-2)',
            maxWidth: 280,
            margin: '0 auto 28px',
            lineHeight: 1.5,
          }}
        >
          подписка на месяц активна. ключ уже у тебя — осталось вставить его в приложение
        </p>

        <div
          style={{
            background: '#fbf8f1',
            border: '1px solid var(--paper-3)',
            borderRadius: 18,
            padding: 20,
            marginBottom: 14,
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 14 }}>
            чек
          </div>
          <Row label="срок" value={daysLabel} />
          <Row label="действует до" value={expires || '—'} />
          <Row label="способ оплаты" value={methodLabel(method)} />
          <Row label="оплачено" value={paidAmount(me, method)} last />
        </div>

        <button
          onClick={onInstall}
          className="btn btn-moss"
          style={{ width: '100%', height: 54, marginBottom: 10, fontSize: 14.5 }}
        >
          <Icon name="bolt" size={16} stroke="#fff" sw={1.8} />
          установить и подключиться
        </button>

        <button
          onClick={onHome}
          className="btn btn-ghost"
          style={{ width: '100%', height: 54, fontSize: 14 }}
        >
          на главную
        </button>

        <div
          style={{
            marginTop: 24,
            fontSize: 11.5,
            color: 'var(--ink-3)',
            textAlign: 'center',
            lineHeight: 1.6,
          }}
        >
          подтверждение и ссылка на ключ отправлены
          <br />
          в чат с ботом · можно вернуться когда удобно
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '11px 0',
        borderBottom: last ? 'none' : '1px solid var(--paper-3)',
        fontSize: 13.5,
      }}
    >
      <span style={{ color: 'var(--ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}
